import { Button } from "@mui/material"
import { useDialog } from "lib/@hooks/useDialog"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { DraftStatus } from "authoring/lib/enum/draft-status.mjs"
import { isLockedToLegacy } from "authoring/lib/enum/project-phase.mjs"
import { bulkGetMissingRequiredDraftAmends } from "../controller/bulk-get-missing-required-draft-amends"
import { bulkSubmitDraftsForReview } from "../controller/bulk-submit-drafts-for-review"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { SendToReviewDialog } from "../components/send-to-review-dialog"
import { SendToReviewBlockersDialog } from "../components/send-to-review-blockers-dialog"
import { SendToReviewResultsDialog } from "../components/send-to-review-results-dialog"
import Iconify from "minimals-template/components/Iconify"
import { Secure } from "lib/authorization/secure"
import { Frag } from "lib/@components/slot/frag"
import { AUTHORING_EDIT_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

export function SendToReviewButton() {
    const { target: project, resolvedSchedules = [] } = useBoundContext()
    const scheduleIds = project?.scopedScheduleIds ?? []
    const scheduleCount = scheduleIds.length

    const sendToReviewDialog = useDialog(<SendToReviewDialog />, { maxWidth: "md", fullWidth: true })
    const blockersDialog = useDialog(<SendToReviewBlockersDialog />, { maxWidth: "md", fullWidth: true })
    const resultsDialog = useDialog(<SendToReviewResultsDialog />, { maxWidth: "md", fullWidth: true })

    if (isLockedToLegacy(project?.phase)) return null

    return (
        <Secure demand={AUTHORING_EDIT_SCHEDULES_DEMAND} fallback={<Frag />}>
            <Button
                variant="outlined"
                startIcon={<Iconify icon="mdi:send-check" />}
                onClick={handleClick}
                data-cy="send-to-review-button"
            >
                Send to Review
            </Button>
        </Secure>
    )

    async function handleClick() {
        const eligibleSchedules = resolvedSchedules.filter(isEligibleForReview)

        if (!eligibleSchedules.length) {
            await blockersDialog({ schedules: resolvedSchedules, scheduleCount })
            return
        }

        const eligibleIds = eligibleSchedules.map((s) => s.id)
        let amendsChecks
        try {
            amendsChecks = await bulkGetMissingRequiredDraftAmends({
                projectId: project.id,
                scheduleIds: eligibleIds,
            })
        } catch (error) {
            errorSnackbar("Failed to validate schedules for review")
            return
        }

        const amendsCheckMap = new Map((amendsChecks ?? []).map((c) => [c.scheduleId, c]))

        const readySchedules = []
        const notReadySchedules = []

        for (const schedule of eligibleSchedules) {
            const check = amendsCheckMap.get(schedule.id)
            if (!check?.eligible || check.missingAmendsKeys?.length > 0) {
                notReadySchedules.push({
                    ...schedule,
                    missingAmendsKeys: check?.missingAmendsKeys ?? [],
                    reason: check?.reason ?? "Validation failed",
                })
            } else {
                readySchedules.push(schedule)
            }
        }

        const confirmed = await sendToReviewDialog({ readySchedules, notReadySchedules })
        if (!confirmed || !readySchedules.length) return

        try {
            const result = await bulkSubmitDraftsForReview({
                projectId: project.id,
                scheduleIds: readySchedules.map((s) => s.id),
            })

            if (result?.submitted?.length && !result?.failed?.length) {
                successSnackbar(
                    `${result.submitted.length} schedule${result.submitted.length !== 1 ? "s" : ""} submitted for review`
                )
            }

            if (result?.failed?.length || result?.skipped?.length) {
                await resultsDialog({
                    submitted: result.submitted ?? [],
                    failed: result.failed ?? [],
                    skipped: result.skipped ?? [],
                })
            }
        } catch (error) {
            errorSnackbar(error?.message ?? "Failed to submit schedules for review")
        }
    }
}

function isEligibleForReview(schedule) {
    return schedule?.isDraft && schedule?.status === DraftStatus.EDITING
}
